import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Cpu, Sparkles, Zap, Package, ShieldCheck, TrendingUp, TrendingDown, Activity,
  AlertCircle, CheckCircle2, ArrowRight, Battery, HardDrive, Star, Layers,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, CartesianGrid, Tooltip, RadialBarChart, RadialBar, PolarAngleAxis } from "recharts";
import { endpoints } from "@/lib/api";
import { usePersona } from "@/lib/personaStore";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import DataSourcesPopover from "@/components/DataSourcesPopover";

const AIPC_DATA_SOURCES = [
  { label: "Devices assigned to you",         mocked: false, source: "Mongo · db.assigned_devices",         prodSource: "ServiceNow CMDB adapter" },
  { label: "Device UX score & trend per device",    mocked: true,  source: "Seed value on device doc",             prodSource: "Nexthink Device UX Real-time API" },
  { label: "NPU TOPS · RAM · Battery",        mocked: true,  source: "Heuristic from device name/brand",     prodSource: "Intel EMA / vPro · OEM APIs (Apple ABM, Dell TechDirect, Lenovo Vantage)" },
  { label: "AI Relevance Score",              mocked: false, source: "Computed live from device profile",    prodSource: "Same algorithm, real inputs" },
  { label: "NPU Utilization %",               mocked: true,  source: "Derived from NPU TOPS on backend",     prodSource: "Nexthink telemetry · Windows Perf Counters · Intel VTune NPU" },
  { label: "AI apps catalogue (37 apps)",     mocked: true,  source: "Static seed in ai_marketplace_data.py",prodSource: "Marketplace catalog (governed by IT) · Intel OpenVINO validated set" },
  { label: "Live per-app CPU · NPU · Latency · Memory", mocked: true, source: "Randomised · biased by app baseline", prodSource: "Nexthink Real-time · Windows ETW providers" },
  { label: "Apps supported vs missing",       mocked: false, source: "Live filter · catalog × device profile", prodSource: "Same, once catalog + profile are real" },
  { label: "Persona average Device UX",             mocked: true,  source: "Hard-coded lookup",                    prodSource: "Nexthink cohort aggregation" },
  { label: "Recommended models (12 curated)", mocked: true,  source: "Static seed in ai_marketplace_data.py",prodSource: "Flexspace Model Registry · governed by IT + Data Platform" },
];

function DexRing({ score, size = 90 }) {
  const color = score >= 85 ? "#0F9B7F" : score >= 70 ? "#0033A0" : score >= 55 ? "#F59E0B" : "#DA291C";
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <ResponsiveContainer width="100%" height="100%">
        <RadialBarChart innerRadius="70%" outerRadius="100%" data={[{ value: score, fill: color }]} startAngle={90} endAngle={-270}>
          <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
          <RadialBar background dataKey="value" cornerRadius={20} />
        </RadialBarChart>
      </ResponsiveContainer>
      <div className="absolute inset-0 grid place-items-center">
        <div className="text-center">
          <div className="font-display font-black text-slate-900" style={{ fontSize: size / 3.5 }}>{score}</div>
          <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">Device UX</div>
        </div>
      </div>
    </div>
  );
}

function relLabel(score) {
  if (score >= 85) return { label: "Elite AI PC", color: "#0033A0" };
  if (score >= 75) return { label: "AI PC Ready", color: "#0F9B7F" };
  if (score >= 60) return { label: "Copilot Capable", color: "#F59E0B" };
  if (score >= 40) return { label: "Basic AI", color: "#94a3b8" };
  return { label: "Non-AI", color: "#94a3b8" };
}

function AppLine({ a }) {
  return (
    <div className="border border-slate-200 rounded-md p-2.5 hover:border-[#0033A0]/40 transition-colors">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold text-slate-900 text-sm truncate">{a.name}</div>
          <div className="text-[11px] text-slate-500">{a.vendor} · {a.category_label}</div>
        </div>
        <Badge variant="outline" className="text-[9px] shrink-0 border-slate-200 text-slate-600">{a.tops_required}+ TOPS</Badge>
      </div>
      <div className="grid grid-cols-4 gap-2 mt-2 text-[10px]">
        <div>
          <div className="uppercase tracking-widest text-slate-400 font-semibold">CPU</div>
          <div className="font-mono font-bold text-slate-900">{a.current_cpu_pct}%</div>
        </div>
        <div>
          <div className="uppercase tracking-widest text-[#0033A0] font-semibold">NPU</div>
          <div className="font-mono font-bold text-[#0033A0]">{a.current_npu_pct}%</div>
        </div>
        <div>
          <div className="uppercase tracking-widest text-slate-400 font-semibold">Latency</div>
          <div className="font-mono font-bold text-slate-900">{a.current_latency_ms}ms</div>
        </div>
        <div>
          <div className="uppercase tracking-widest text-slate-400 font-semibold">Mem</div>
          <div className="font-mono font-bold text-slate-900">{a.current_mem_mb}MB</div>
        </div>
      </div>
    </div>
  );
}

function DeviceCard({ dev }) {
  const rl = relLabel(dev.relevance.score);
  const dexColor = dev.dex_score >= 85 ? "text-[#0F9B7F]" : dev.dex_score >= 70 ? "text-[#0033A0]" : dev.dex_score >= 55 ? "text-[#F59E0B]" : "text-[#DA291C]";
  const trendIcon = dev.dex_trend === "up" ? <TrendingUp size={12} className="text-[#0F9B7F]" /> : dev.dex_trend === "down" ? <TrendingDown size={12} className="text-[#DA291C]" /> : <Activity size={12} className="text-slate-400" />;
  return (
    <Card
      data-testid={`my-device-${dev.asset_tag}`}
      className="p-5 border-slate-200 shadow-sm flex flex-col"
    >
      <div className="flex items-start gap-4">
        <DexRing score={dev.dex_score} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold font-mono">{dev.asset_tag}</span>
            {trendIcon}
            <span className={`text-[10px] font-semibold ${dexColor}`}>{dev.dex_trend?.toUpperCase()}</span>
          </div>
          <div className="font-display font-bold text-slate-900 text-lg mt-0.5 leading-tight">{dev.device_name}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {dev.brand} · {dev.category} · {dev.location || "unassigned"} · {dev.age_months}mo old
          </div>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <Badge
              className="text-[10px] text-white"
              style={{ backgroundColor: rl.color }}
            >
              AI {dev.relevance.score} · {rl.label}
            </Badge>
            {dev.profile.copilot_plus_ready && (
              <Badge className="bg-[#0033A0] hover:bg-[#0033A0] text-white text-[9px]">
                <Sparkles size={9} className="mr-1" /> Copilot+
              </Badge>
            )}
          </div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-4 gap-3 text-center">
        <div className="border border-slate-200 rounded-md p-2">
          <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">NPU TOPS</div>
          <div className="font-mono font-bold text-slate-900 text-sm">{dev.profile.npu_tops}</div>
        </div>
        <div className="border border-slate-200 rounded-md p-2">
          <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">RAM</div>
          <div className="font-mono font-bold text-slate-900 text-sm">{dev.profile.ram_gb} GB</div>
        </div>
        <div className="border border-slate-200 rounded-md p-2">
          <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">Battery</div>
          <div className="font-mono font-bold text-slate-900 text-sm">{dev.profile.battery_hours || "—"}{dev.profile.battery_hours ? "h" : ""}</div>
        </div>
        <div className="border border-slate-200 rounded-md p-2">
          <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">NPU Util</div>
          <div className="font-mono font-bold text-[#0033A0] text-sm">{dev.npu_utilization_pct}%</div>
        </div>
      </div>

      {/* Apps running on this device — laptops only. AI apps aren't meaningful
          for phone/tablet form factors, so we hide this block on those devices. */}
      {dev.category === "laptop" && dev.apps_running?.length > 0 && (
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5">
            <Zap size={11} className="text-[#0033A0]" /> Live AI apps on this device
          </div>
          <div className="mt-2 space-y-1.5" data-testid={`apps-running-${dev.asset_tag}`}>
            {dev.apps_running.slice(0, 4).map((a) => <AppLine key={a.id} a={a} />)}
          </div>
        </div>
      )}

      {/* Missing apps — also gated to laptops for the same reason. */}
      {dev.category === "laptop" && dev.apps_missing_count > 0 && (
        <div className="mt-4 border border-amber-200 bg-amber-50/50 rounded-md p-3">
          <div className="flex items-start gap-2">
            <AlertCircle size={14} className="text-[#B45309] shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-[11px] font-semibold text-[#B45309]">
                {dev.apps_missing_count} AI app{dev.apps_missing_count !== 1 ? "s" : ""} for your persona won&apos;t run on this device
              </div>
              <div className="text-[10px] text-slate-600 mt-1 line-clamp-2">
                {dev.apps_missing.slice(0, 3).map((a) => a.name).join(" · ")}{dev.apps_missing.length > 3 ? "…" : ""}
              </div>
              <Link to="/catalogue" className="text-[10px] text-[#0033A0] font-semibold inline-flex items-center gap-1 mt-1.5 hover:underline">
                Explore an upgrade <ArrowRight size={10} />
              </Link>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

export default function MyAIPC() {
  const { persona } = usePersona();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    endpoints.myAipc(persona.id).then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [persona.id]);

  if (loading || !data) {
    return (
      <div className="space-y-6" data-testid="my-aipc-page">
        <Skeleton className="h-12 w-96" />
        <div className="grid grid-cols-4 gap-4">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-24" />)}</div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  const k = data.kpis;
  const compareDelta = k.vs_persona_avg;

  return (
    <div className="space-y-6" data-testid="my-aipc-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-2">
              <Cpu size={12} className="text-[#0033A0]" /> MyPC · Personal View
            </div>
            <DataSourcesPopover
              rows={AIPC_DATA_SOURCES}
              title="Where the AI PC data comes from"
              pill="Data sources"
            />
          </div>
          <h1 className="font-display font-black text-3xl text-slate-900 tracking-tight mt-1">
            How your device is performing — and what AI it can run
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-3xl">
            Everything shown here is scoped to <span className="font-semibold text-slate-900">{persona.name}</span>. See your live Device UX score, per-app resource usage on the NPU, Copilot+ readiness and which persona-recommended AI apps your current device can (and can&apos;t) run.
          </p>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="my-aipc-kpis">
        <Card className="p-5 bg-[#0033A0] text-white border-transparent shadow-sm">
          <div className="flex items-center justify-between text-white/70">
            <span className="text-[11px] uppercase tracking-widest font-semibold">My devices</span>
            <Package size={14} />
          </div>
          <div className="font-display font-black text-3xl mt-1">{k.devices}</div>
          <div className="text-xs mt-1 text-white/70">assigned to you</div>
        </Card>
        <Card className="p-5 border-slate-200 shadow-sm">
          <div className="flex items-center justify-between text-slate-500">
            <span className="text-[11px] uppercase tracking-widest font-semibold">Avg Device UX</span>
            <Activity size={14} />
          </div>
          <div className="font-display font-black text-3xl mt-1 text-slate-900">{k.avg_dex}</div>
          <div className="text-xs mt-1 flex items-center gap-1">
            {compareDelta >= 0 ? <TrendingUp size={11} className="text-[#0F9B7F]" /> : <TrendingDown size={11} className="text-[#DA291C]" />}
            <span className={`font-semibold ${compareDelta >= 0 ? "text-[#0F9B7F]" : "text-[#DA291C]"}`}>{compareDelta >= 0 ? "+" : ""}{compareDelta}</span>
            <span className="text-slate-500">vs {persona.name} avg ({data.persona_average_dex})</span>
          </div>
        </Card>
        <Card className="p-5 border-slate-200 shadow-sm">
          <div className="flex items-center justify-between text-slate-500">
            <span className="text-[11px] uppercase tracking-widest font-semibold">AI-ready devices</span>
            <Sparkles size={14} />
          </div>
          <div className="font-display font-black text-3xl mt-1 text-slate-900">{k.ai_ready_devices}<span className="text-slate-400 text-lg"> / {k.devices}</span></div>
          <div className="text-xs mt-1 text-slate-500">Relevance ≥ 75</div>
        </Card>
        <Card className="p-5 border-slate-200 shadow-sm">
          <div className="flex items-center justify-between text-slate-500">
            <span className="text-[11px] uppercase tracking-widest font-semibold">Copilot+ devices</span>
            <Cpu size={14} />
          </div>
          <div className="font-display font-black text-3xl mt-1 text-slate-900">{k.copilot_plus_devices}<span className="text-slate-400 text-lg"> / {k.devices}</span></div>
          <div className="text-xs mt-1 text-slate-500">≥ 40 TOPS NPU</div>
        </Card>
      </div>

      {/* Device health snapshot (moved here from Reports so all device info is on one tab) */}
      <Card className="p-6 border-slate-200 shadow-sm">
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5">
          <Package size={12} className="text-[#0033A0]" /> Your device health
        </div>
        <h2 className="font-display font-bold text-xl text-slate-900 mt-1">Device UX per device</h2>
        <div className="mt-4 space-y-2" data-testid="my-device-health">
          {data.my_devices.length === 0 && <div className="text-slate-500 text-sm">No devices assigned.</div>}
          {data.my_devices.map((d) => {
            const score = d.dex_score ?? 0;
            const color = score >= 85 ? "#0F9B7F" : score >= 70 ? "#0033A0" : score >= 55 ? "#F59E0B" : "#DA291C";
            return (
              <div key={d.asset_tag} className="border border-slate-200 rounded-md p-3 flex items-center gap-3">
                <div className="w-12 h-12 rounded-md text-white grid place-items-center font-black text-sm shrink-0" style={{ backgroundColor: color }}>
                  {score}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 text-sm truncate">{d.device_name}</div>
                  <div className="text-[11px] text-slate-500 font-mono">{d.asset_tag} · {d.age_months}mo · {d.status}</div>
                </div>
                {d.dex_trend && (
                  <Badge variant="outline" className="text-[9px] border-slate-200 text-slate-600 capitalize">{d.dex_trend}</Badge>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      {/* Devices */}
      <div>
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
          <Zap size={12} className="text-[#0033A0]" /> Your devices
        </div>
        <h2 className="font-display font-bold text-xl text-slate-900 mt-1">Live AI usage · per device · per app</h2>
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mt-4" data-testid="my-devices-grid">
          {data.my_devices.length === 0 && (
            <Card className="p-6 border-slate-200 shadow-sm text-center">
              <div className="text-slate-500 text-sm">No devices assigned. Head to the <Link to="/catalogue" className="text-[#0033A0] font-semibold hover:underline">Catalogue</Link> to place a request.</div>
            </Card>
          )}
          {data.my_devices.map((d) => <DeviceCard key={d.asset_tag} dev={d} />)}
        </div>
      </div>

      {/* Recommended models */}
      {data.recommended_models?.length > 0 && (
        <Card className="p-6 border-slate-200 shadow-sm" data-testid="my-recommended-models">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
            <Layers size={12} className="text-[#0033A0]" /> Recommended models for {persona.name}
          </div>
          <h2 className="font-display font-bold text-xl text-slate-900 mt-1">Local models curated for your role</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
            {data.recommended_models.map((m) => (
              <div key={m.id} className="border border-slate-200 rounded-md p-3 hover:border-[#0033A0]/40 transition-colors">
                <div className="text-[10px] uppercase tracking-widest text-[#0033A0] font-semibold">{m.modality}</div>
                <div className="font-display font-bold text-slate-900 text-sm mt-0.5 leading-tight">{m.name}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">{m.vendor} · {m.params_b ? `${m.params_b}B` : "—"} · {m.quantization}</div>
                <div className="text-[11px] text-slate-600 mt-2 line-clamp-2 leading-relaxed">{m.notes}</div>
                {m.npu_ready && (
                  <Badge className="mt-2 bg-[#0033A0] hover:bg-[#0033A0] text-white text-[9px]">
                    <Cpu size={9} className="mr-1" /> NPU ready
                  </Badge>
                )}
              </div>
            ))}
          </div>
          <div className="mt-3">
            <Link to="/ai-marketplace">
              <Button variant="outline" size="sm" data-testid="my-aipc-goto-marketplace" className="border-slate-300">
                Browse full model registry <ArrowRight size={13} className="ml-1" />
              </Button>
            </Link>
          </div>
        </Card>
      )}
    </div>
  );
}
